import { Form } from "antd"
import { Group, Modal, notification } from "components/common"
import { useAddTaskMutation, useUpdateTaskMutation } from "features/tasks/api"
import { t } from "i18next"
import { useState } from "react"
import { ACTION_TYPES } from "utils/gtm/gtmEventsData"
import { useLazyGetLeadDetailsQuery } from "features/leads/api"
import { fireEvent } from "utils/gtm/gtmEventHandler"
import ClientDetailBar from "./components/clientDetailBar/clientDetailBar"
import AddTaskForm from "./components/addTaskForm"

export default function AddTaskModal({ lead, client, formRef, from = {}, isEditable }) {
  const [getLeadDetails, { data: leadDetailInfo, isFetching: isFetchingLead }] = useLazyGetLeadDetailsQuery()
  const [addTask, { isLoading, isError, error, reset: resetErrorForAddTask }] = useAddTaskMutation()

  const [
    updateTask,
    {
      isLoading: isLoadingForUpdateTask,
      isError: isErrorForUpdateTask,
      error: errorForUpdateTask,
      reset: resetErrorForUpdateTask,
    },
  ] = useUpdateTaskMutation()

  const [form] = Form.useForm()
  const verifyMeeting = Form.useWatch("verifyMeeting", form)
  const project = Form.useWatch("project", form)
  let recorderControls = null

  const [busySlotVisible, setBusySlotVisible] = useState(false)

  const onModalVisible = () => {
    getLeadDetails({
      leadId: lead?.id || lead?.inquiryIds || lead,
      ...(isEditable === true && { detailsType: "edit_task" }),
    })
      .unwrap()
      .catch(e => notification?.error({ message: e?.message }))
  }

  const onSubmit = async ({ values, taskType, nextTask }) => {
    const eventPayload = {
      client_name: lead?.Client?.name || client?.name,
      project_name: values?.project,
      task_type: taskType,
      attachment: !!values?.attachment ? "yes" : "no",
      comments: values?.comment,
      next_task: nextTask,
      lead_id: lead?.id,
    }
    try {
      let response
      if (isEditable) {
        response = await updateTask({
          taskId: leadDetailInfo?.tasks?.[0]?.id,
          encodedFile: values?.attachment,
          ...values,
        })
          .unwrap()
          .then(() => {
            notification.success({
              message: t("Task Updated Successfully"),
            })
          })
      } else {
        response = await addTask({
          lead,
          encodedFile: values?.attachment,
          verifyMeeting,
          ...values,
        })
          .unwrap()
          .then(() => {
            notification.success({
              message: t("Task Added Successfully"),
            })
          })
      }

      if (!response?.error) {
        formRef?.current?.setVisible(false)

        resetErrorForAddTask()
        fireEvent({
          ...ACTION_TYPES.task_tab_submit_task,
          ...from,
          response: "200",
          client_id: client.id,
          lead_id: lead.id,
          ...eventPayload,
        })
        setBusySlotVisible(false)
      }
    } catch (e) {
      notification.error({ message: e?.message })
      fireEvent({
        ...ACTION_TYPES.task_tab_submit_task,
        ...from,
        response: e.status,
        client_id: client.id,
        lead_id: lead.id,
        ...eventPayload,
      })
    }
  }

  return (
    <Modal
      title={isEditable ? t("Edit Task") : t("Add Task")}
      ref={formRef}
      okText={t("Submit")}
      width={780}
      cancelText={t("Cancel")}
      loading={isLoadingForUpdateTask || isLoading}
      onOk={form.submit}
      onModalVisible={onModalVisible}
      footerAlert={error}
      destroyOnClose={true}
      scrollToFirstError
      onCancel={() => {
        recorderControls?.stopRecording()
        setBusySlotVisible(false)
        resetErrorForAddTask()
        formRef?.current?.setVisible(false)
      }}
    >
      <Group>
        <ClientDetailBar client={client} lead={lead} project={project} leadDetailInfo={leadDetailInfo} />
        <AddTaskForm
          form={form}
          lead={lead}
          isEditable={isEditable}
          leadDetailInfo={leadDetailInfo}
          setBusySlotVisible={setBusySlotVisible}
          busySlotVisible={busySlotVisible}
          onSubmit={onSubmit}
          recorderControls
          formRef={formRef}
        />
      </Group>
    </Modal>
  )
}
